<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import ReportPreview from '@/components/chat/ReportPreview.vue'
import { useChatWebSocket } from '@/composables/useChatWebSocket'

const route = useRoute()
const orgId = computed(() => route.params.org_id)
const sessionCode = computed(() => route.params.session_code)

// State
const currentReport = ref(null)
const currentSession = ref(null)
const layerData = ref(null)
const layerFeatures = ref([])

// WebSocket for real-time streaming
const {
  isConnected: wsConnected,
  isStreaming: wsStreaming,
  streamingReport,
  connectionError,
  connect: connectWebSocket,
  disconnect: disconnectWebSocket,
  onStreamComplete
} = useChatWebSocket()

// Load session details
const loadSession = async () => {
  if (!sessionCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}`
    })

    const sessionData = response.data
    currentSession.value = sessionData

    // Load layer data
    await loadLayerData(sessionData.layer_code)

    // Load the associated report
    await loadSessionReport()

    // Connect to WebSocket for real-time updates
    try {
      await connectWebSocket(orgId.value, sessionCode.value)
      console.log('[Reports] WebSocket connected successfully')
    } catch (error) {
      console.error('[Reports] WebSocket connection failed:', error)
      // Don't show toast - it's already shown by ReportsList
    }
  } catch (error) {
    console.error('Error loading session:', error)
    toast({
      title: 'Error',
      description: 'Failed to load session details',
      variant: 'destructive'
    })
  }
}

// Load layer data and features
const loadLayerData = async (layerCode) => {
  // Skip if layerCode is null or undefined
  if (!layerCode) {
    console.warn('loadLayerData called with no layerCode')
    return
  }

  try {
    // Load layer metadata
    const layerResponse = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${layerCode}`
    })
    layerData.value = layerResponse.data

    // Load layer features
    const featuresResponse = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${layerCode}/features`
    })
    layerFeatures.value = featuresResponse.data.features || []
  } catch (error) {
    console.error('Error loading layer data:', error)
    // Don't show error toast - layer data is optional context
  }
}

// Load session report
const loadSessionReport = async () => {
  if (!sessionCode.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/chats/${sessionCode.value}/report`
    })

    currentReport.value = response.data
  } catch (error) {
    console.error('Error loading report:', error)
    // Report might not exist yet, that's ok
  }
}

// Handle stream completion
onStreamComplete((data) => {
  console.log('[Reports] Stream complete, reloading report:', data)

  // Reload the report to get the saved version
  loadSessionReport()

  toast({
    title: 'Report updated',
    description: 'New version generated successfully',
    variant: 'default'
  })
})

// Watch for session code changes
watch(sessionCode, (newCode, oldCode) => {
  if (newCode && newCode !== oldCode) {
    loadSession()
  } else if (!newCode) {
    // Disconnect WebSocket when leaving
    disconnectWebSocket()
  }
})

// On mount
onMounted(async () => {
  await loadSession()
})

// Cleanup
onUnmounted(() => {
  disconnectWebSocket()
})
</script>

<template>
  <div class="h-full overflow-hidden">
    <ReportPreview
      :report="currentReport"
      :is-updating="false"
      :org-id="orgId"
      :session-code="sessionCode"
      :is-streaming="wsStreaming"
      :streaming-report="streamingReport"
      @version-changed="(versionData) => currentReport = versionData"
    />
  </div>
</template>
